import React, { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Booth, BoothJobInfo, CreateBoothInput, VJFPUser } from 'app/API';
import { useAppContext } from 'app/context/app-context';
import { Amplify, Storage } from 'aws-amplify';
import { useAuth } from 'app/context/auth-context';
import QueueQuestionFormComponent from 'app/modules/shared/manage-booth/QueueQuestions/QueueQuestions';
import { Button, Tab, Tabs } from 'react-bootstrap-v5';
import JobsFormComponent from 'app/modules/shared/manage-booth/JobsForm/JobsForm';
import { Roles } from 'app/types';
import { ONE_MIN_IN_MILLISECONDS } from 'app/helpers/MiscUtils';

Amplify.configure({
    Storage: {
        AWSS3: {
            region: 'us-east-1',
            bucket: 'videojobfairs-docs'
        }
    }
});
interface CreateEditBoothFormProps {
    onSubmit: (values: CreateBoothInput) => void;
    booth?: Booth,
    companyImageUrl?: Promise<string> | string,
    orgName: string
}

const CreateEditBooth: React.FC<CreateEditBoothFormProps> = ({ onSubmit, booth, companyImageUrl, orgName }) => {
    const isEditMode = !!booth;
    const { jobFair } = useAppContext();
    const [logoThumbnail, setLogoThumbnail] = useState<string | null>(null);
    const [isLogoUploading, setIsLogoUploading] = useState(false);
    const { user } = useAuth();
    const queueQuestionsRef = useRef<{ submitQueueQuestionForm: () => Promise<void>, questionsLength: number }>(null);
    const jobsRef = useRef<{ getJobs: () => BoothJobInfo[], jobsLength: number }>(null);
    const editPermissionList = {
        name: [Roles.EmployerAdmin, Roles.Admin],
        description: [Roles.EmployerAdmin, Roles.Admin],
        linkToJobBoard: [Roles.EmployerAdmin, Roles.Admin],
        hotJobSummary: [Roles.EmployerAdmin, Roles.Admin],
        chatDuration: [Roles.EmployerAdmin, Roles.Admin],
        isFeatured: [Roles.EmployerAdmin, Roles.Admin],
        companyLogo: [Roles.EmployerAdmin, Roles.Admin],
        featuredBooth: [Roles.EmployerAdmin, Roles.Admin],
        minimumScore: [Roles.EmployerAdmin, Roles.Admin],
        queueQuestions: [Roles.EmployerAdmin, Roles.Admin],
        jobs: [Roles.EmployerAdmin, Roles.Recruiter, Roles.Admin],
    }

    useEffect(() => {
        if (!jobFair?.id) {
            return;
        }

        if (booth != null) {
            try {
                // Set initial form values
                formik.setValues({
                    name: booth?.name || orgName + ' - booth' || '',
                    jobFairId: jobFair.id,
                    isFeatured: booth?.isFeatured || false,
                    chatDuration: (booth?.chatDuration || 0) / ONE_MIN_IN_MILLISECONDS,
                    companyLogo: null as File | null,
                    linkToJobBoard: booth?.linkToJobBoard || 'https://',
                    minimumScore: booth?.minimumScore || 0,
                    description: booth?.description || '',
                    hotJobSummary: booth?.hotJobSummary || '',
                    createdBy: booth?.createdBy || '',
                    recruiters: booth?.recruiters || [],
                    sortOrder: booth?.sortOrder || 0,
                });

                if (companyImageUrl && companyImageUrl instanceof Promise) {
                    companyImageUrl.then(url => {
                        setLogoThumbnail(url);
                    });
                } else if (companyImageUrl) {
                    setLogoThumbnail(companyImageUrl as string);
                }

            } catch (error) {
                console.error('Error fetching organization data:', error);
            }
        }
    }, [booth]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.currentTarget.files;
        if (files && files.length > 0) {
            const file = files[0];
            formik.setFieldValue('companyLogo', file);
            setIsLogoUploading(true);
            // Create thumbnail
            const reader = new FileReader();
            reader.onload = (e) => {
                setLogoThumbnail(e.target?.result as string);
                setIsLogoUploading(false);
            };
            if (file instanceof Blob) {
                reader.readAsDataURL(file);
            }
        }
    };

    const formik = useFormik({
        initialValues: {
            name: booth?.name || '',
            jobFairId: jobFair ? jobFair.id : '',
            isFeatured: booth?.isFeatured || false,
            chatDuration: (booth?.chatDuration || 0) / ONE_MIN_IN_MILLISECONDS,
            companyLogo: null as File | null,
            linkToJobBoard: booth?.linkToJobBoard || 'https://',
            minimumScore: booth?.minimumScore || 0,
            description: booth?.description || '',
            hotJobSummary: booth?.hotJobSummary || '',
            createdBy: booth?.createdBy || '',
            recruiters: booth?.recruiters || [],
            sortOrder: booth?.sortOrder || 0,
        },
        validationSchema: Yup.object({
            name: Yup.string().required('Required').max(100, 'Must be 100 characters or less'),
            isFeatured: Yup.boolean(),
            chatDuration: Yup.number().required('Required').min(1, 'Must be positive'),
            companyLogo: isEditMode ? Yup.mixed() : Yup.mixed().required('Company logo is required'),
            minimumScore: Yup.number().required('Required').min(0, 'Must be positive'),
            description: Yup.string(),
            hotJobSummary: Yup.string(),
            linkToJobBoard: Yup.string().url('Must be a valid URL'),
            recruiters: Yup.array().of(Yup.number().nullable()),
        }),
        onSubmit: async (values) => {
            try {
                if (!user?.id) {
                    return;
                }
                let logoPath;

                if (values.companyLogo) {
                    try {
                        const file = values.companyLogo;
                        const fileExtension = file.name.split('.').pop();

                        logoPath = await Storage.put(`${jobFair!.companyId}/company-logo/${new Date().getTime()}.${fileExtension}`, file, {
                            contentType: file.type
                        });
                    } catch (err) {
                        console.error('Error uploading logo:', err);
                        throw err;
                    }
                }

                if (isEditMode && queueQuestionsRef.current) {
                    await queueQuestionsRef.current.submitQueueQuestionForm();
                }

                const { companyLogo, ...rest } = values;
                const updatedBooth: CreateBoothInput = {
                    ...rest,
                    organizationId: jobFair!.companyId,
                    companyImageUrl: logoPath?.key || booth?.companyImageUrl || '',
                    jobs: jobsRef.current?.jobsLength ? jobsRef.current.getJobs() : booth?.jobs || [],
                    chatDuration: (values.chatDuration || 0) * ONE_MIN_IN_MILLISECONDS
                };

                onSubmit(updatedBooth);

            } catch (error) {
                console.error('Error submitting form', error);
            }
        },
    });

    return (
        <form onSubmit={formik.handleSubmit}>
            <div className='row'>
                <div className='col-md-6'>
                    <div className='fv-row mb-7'>
                        <label className='required fw-bold fs-6 mb-2'>Booth name</label>
                        <input
                            type="text"
                            id="name"
                            {...formik.getFieldProps('name')}
                            className={`form-control form-control-solid mb-3 mb-lg-0 ${formik.touched.name && formik.errors.name ? 'is-invalid' : ''
                                }`}
                            disabled={!editPermissionList.name.some((role) => user?.roles?.includes(role))}
                        />


                        {formik.touched.name && formik.errors.name && (
                            <div className="text-danger mt-2">{formik.errors.name}</div>
                        )}
                    </div>
                    <div className='fv-row mb-7'>
                        <label className='fw-bold fs-6 mb-2'>Description</label>
                        <textarea
                            id="description"
                            {...formik.getFieldProps('description')}
                            className="form-control form-control-solid"
                            rows={4}
                            disabled={!editPermissionList.description.some((role) => user?.roles?.includes(role))}
                        />
                    </div>

                    <div className='fv-row mb-7'>
                        <label className='fw-bold fs-6 mb-2'>Link to job board</label>
                        <input
                            type="text"
                            id="linkToJobBoard"
                            {...formik.getFieldProps('linkToJobBoard')}
                            className={`form-control form-control-solid mb-3 mb-lg-0 ${formik.touched.linkToJobBoard && formik.errors.linkToJobBoard ? 'is-invalid' : ''
                                }`}
                            disabled={!editPermissionList.linkToJobBoard.some((role) => user?.roles?.includes(role))}
                        />
                        <Button variant="link" title='Browse to Job Board' className='text-primary mb-2' href={formik.values.linkToJobBoard} target="_blank" rel="noopener noreferrer">
                          Browse to Job Board: &nbsp; <i className="fas fa-external-link-alt me-2"></i>
                        </Button>
                    </div>

                </div>
                <div className='col-md-6'>
                    <div className='fv-row mb-7'>
                        <label className='fw-bold fs-6 mb-2'>Logo</label>
                        <div className="d-flex align-items-center">
                            <label hidden={!editPermissionList.companyLogo.some((role) => user?.roles?.includes(role))} htmlFor="companyLogo" className="btn btn-primary me-3">
                                <i className="fas fa-upload me-2"></i>Select Logo
                                <input
                                    id="companyLogo"
                                    name="companyLogo"
                                    type="file"
                                    accept="image/*"

                                    className="d-none"
                                    onChange={handleFileChange}
                                    disabled={!editPermissionList.companyLogo.some((role) => user?.roles?.includes(role))}
                                />
                            </label>
                            {isLogoUploading ? (
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            ) : logoThumbnail && (
                                <div className="ms-3">
                                    <img
                                        src={logoThumbnail}
                                        alt="Logo preview"
                                        crossOrigin="anonymous"
                                        style={{ maxWidth: '100px', maxHeight: '100px' }}
                                        onError={(e) => console.error('Error loading image:', e)}
                                    />
                                </div>
                            )}
                        </div>
                        {formik.touched.companyLogo && formik.errors.companyLogo && (
                            <div className="text-danger mt-2">{formik.errors.companyLogo}</div>
                        )}
                    </div>

                    <div className='fv-row mb-7'>
                        <label className='required fw-bold fs-6 mb-2'>Default Interview Duration (minutes)</label>
                        <input
                            type="number"
                            id="chatDuration"
                            {...formik.getFieldProps('chatDuration')}
                            className={`form-control form-control-solid ${formik.touched.chatDuration && formik.errors.chatDuration ? 'is-invalid' : ''
                                }`}
                            disabled={!editPermissionList.chatDuration.some((role) => user?.roles?.includes(role))}
                        />

                        {formik.touched.chatDuration && formik.errors.chatDuration && (
                            <div className="text-danger mt-2">{formik.errors.chatDuration}</div>
                        )}
                    </div>

                    {/* <div className='fv-row mb-7'>
                        <div className="form-check form-switch">
                            <input
                                type="checkbox"
                                id="isFeatured"
                                {...formik.getFieldProps('isFeatured')}
                                className="form-check-input"
                                checked={formik.values.isFeatured}
                                disabled={!editPermissionList.featuredBooth.some((role) => user?.roles?.includes(role))}

                            />
                            <label className="form-check-label" htmlFor="isFeatured">Featured Booth</label>
                        </div>
                    </div> */}
                </div>
            </div>
            {booth &&
                <div>
                    <label className='fw-bold fs-6 mb-2'>Hot Jobs</label>
                    <JobsFormComponent ref={jobsRef} booth={booth}></JobsFormComponent>
                </div>
            }
            {booth &&
                <Tabs defaultActiveKey={editPermissionList.queueQuestions.some((role) => user?.roles?.includes(role)) ? 'QQ' : 'jobForm'}>
                    <Tab eventKey='QQ' title="Queue Questions" disabled={!editPermissionList.queueQuestions.some((role) => user?.roles?.includes(role))}>
                        <div className='row'>
                            <label className='form-label fs-6 fw-bolder text-dark'></label>
                            <p>Enter or edit the queue questions below that job seekers will be asked when they enter your booth queue.</p>
                            <QueueQuestionFormComponent ref={queueQuestionsRef} booth={booth}
                            ></QueueQuestionFormComponent>
                            <h6>Job seeker must meet this minimum score to be placed in booth queue.</h6>
                            <div className='fv-row mt-2 mb-5'>
                                <input
                                    id="number-input-field"
                                    className='form-control form-control-lg'
                                    placeholder='type here to add the question...'
                                    {...formik.getFieldProps('minimumScore')}
                                    type="number"
                                    name='minimumScore'
                                    min={0}
                                    autoComplete='off'
                                    disabled={!editPermissionList.minimumScore.some((role) => user?.roles?.includes(role))}
                                />

                                {formik.touched.minimumScore && formik.errors.minimumScore && (
                                    <div className='fv-plugins-message-container'>
                                        <span role='alert'>{formik.errors.minimumScore}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Tab>

                </Tabs>

            }
            <div className='text-center pt-15'>
                <button
                    type="submit"
                    className="btn btn-primary"
                >
                    {isEditMode ? 'Update' : 'Create Booth'}
                </button>
            </div>
        </form >
    );
};

export default CreateEditBooth;
